/**
 * Mobile banner offering to add the app to the home screen (owner request 2026-09-28). Shown on phones only, when the
 * app is not already running installed, and not within 30 days of "לא עכשיו" (per device, localStorage).
 * Android / Chrome: the browser's own install prompt (`beforeinstallprompt`, captured at module load because it can fire
 * before React mounts). iPhone / iPad (no prompt API): short Share → "הוספה למסך הבית" steps. Other browsers: menu steps.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { Download, Share, X } from 'lucide-react';
import { Icon3D } from '@/components/Icon3D';
import { Button } from '@/components/ui/button';

interface InstallPromptEvent extends Event { prompt: () => Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> }

const KEY_DISMISSED = 'installBanner.dismissedAt';
const KEY_INSTALLED = 'installBanner.installed';
const SNOOZE_DAYS = 30;

let deferred: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => { e.preventDefault(); deferred = e as InstallPromptEvent; listeners.forEach((l) => l()); });
  window.addEventListener('appinstalled', () => { deferred = null; try { localStorage.setItem(KEY_INSTALLED, '1'); } catch { /* private mode */ } listeners.forEach((l) => l()); });
}

function isStandalone(): boolean {
  return window.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}
const isIos = () => /iPhone|iPad|iPod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isPhone = () => window.matchMedia?.('(max-width: 1023px)').matches && (window.matchMedia?.('(pointer: coarse)').matches ?? true);

function snoozed(): boolean {
  try {
    if (localStorage.getItem(KEY_INSTALLED)) return true;
    const at = Number(localStorage.getItem(KEY_DISMISSED) ?? 0);
    return !!at && Date.now() - at < SNOOZE_DAYS * 86_400_000;
  } catch { return false; }
}

/** screens with their own fixed bottom action (the banner would cover it) */
const NO_BANNER = new Set(['/quick-expense', '/quick-income']);

export function InstallBanner() {
  const { pathname } = useLocation();
  const [, force] = useState(0);
  const [hidden, setHidden] = useState(() => typeof window === 'undefined' || isStandalone() || !isPhone() || snoozed());
  const [help, setHelp] = useState(false);
  useEffect(() => { const l = () => force((n) => n + 1); listeners.add(l); return () => { listeners.delete(l); }; }, []);
  if (hidden || NO_BANNER.has(pathname)) return null;

  const dismiss = () => { try { localStorage.setItem(KEY_DISMISSED, String(Date.now())); } catch { /* private mode */ } setHidden(true); };
  const install = async () => {
    if (!deferred) { setHelp(true); return; }
    await deferred.prompt();
    const { outcome } = await deferred.userChoice;
    deferred = null;
    if (outcome === 'accepted') setHidden(true);
  };
  const ios = isIos();

  return (
    <div className="no-print fixed inset-x-3 bottom-3 z-40 rounded-2xl border border-border bg-surface p-3 shadow-[var(--shadow-card)] lg:hidden" role="dialog" aria-label="התקנת האפליקציה">
      <div className="flex items-start gap-3">
        <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-bg"><Icon3D name="app-logo" size={36} eager /></span>
        <div className="min-w-0 flex-1">
          <div className="font-semibold">לשמור כאפליקציה בטלפון?</div>
          <p className="text-sm text-ink-2">פתיחה בלחיצה אחת מהמסך הראשי, במסך מלא — כמו כל אפליקציה.</p>
        </div>
        <button type="button" onClick={dismiss} className="flex size-9 shrink-0 items-center justify-center rounded-lg text-muted hover:bg-surface-2" aria-label="סגירה"><X className="size-4" /></button>
      </div>
      {help || (ios && !deferred) ? (
        <ol className="mt-2 list-decimal ps-5 text-sm text-ink-2">
          {ios ? (
            <>
              <li>לוחצים על כפתור השיתוף <Share className="inline size-4 align-text-bottom text-primary" /> בתחתית המסך (בספארי).</li>
              <li>גוללים ובוחרים <b>״הוספה למסך הבית״</b>.</li>
              <li>לוחצים <b>״הוסף״</b> — הסמל של בית הכנסת יופיע במסך הראשי.</li>
            </>
          ) : (
            <>
              <li>פותחים את תפריט הדפדפן (שלוש הנקודות ⋮ למעלה).</li>
              <li>בוחרים <b>״התקנת אפליקציה״</b> או <b>״הוספה למסך הבית״</b>.</li>
              <li>מאשרים — הסמל של בית הכנסת יופיע במסך הראשי.</li>
            </>
          )}
        </ol>
      ) : null}
      <div className="mt-2 flex gap-2">
        {!ios || deferred ? <Button className="flex-1" onClick={install}><Download /> {deferred ? 'התקנה' : 'איך מתקינים?'}</Button> : null}
        <Button variant="outline" className={ios && !deferred ? 'flex-1' : ''} onClick={dismiss}>לא עכשיו</Button>
      </div>
    </div>
  );
}
